import {Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MessageTranslateService, NetworkProviderPlansService} from '@shared/services';
import {Subject} from 'rxjs';
import {AceSelectComponent} from '../../ace-select/ace-select.component';
import {finalize} from 'rxjs/operators';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NetworkProviderPlan} from '@shared/services/api/access-sites.interface';

@Component({
  selector: 'app-network-provider-plan-select',
  templateUrl: './network-provider-plan-select.component.html',
  styleUrls: ['./network-provider-plan-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NetworkProviderPlanSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class NetworkProviderPlanSelectComponent implements OnInit, OnChanges, ControlValueAccessor {

  readonly MAX_ITEMS = 3;

  selectedNetworkProviderPlans: NetworkProviderPlan[];
  networkProviderPlans: NetworkProviderPlan[];

  @Input()
  networkProviderTypeId: number;
  @Input()
  id: string;
  @Input()
  required: boolean;
  @Input()
  class: string;
  @Input()
  multiSelect = false;
  @Input()
  placeholder: string;

  loading: boolean;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  @ViewChild('aceSelect', { static: true })
  private aceSelect: AceSelectComponent;

  private onChange: (_) => void = (_) => {};
  private onTouched: () => void = () => {};

  constructor(private translateService: MessageTranslateService,
              private networkProviderPlansService: NetworkProviderPlansService) {
  }

  ngOnInit() {
    this.aceSelect.registerOnChange(this.aceSelectChange);
    this.aceSelect.registerOnTouched(this.aceSelectTouched);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.networkProviderTypeId) {
      this.loading = true;
      this.networkProviderPlansService.getNetworkProviderPlans({network_provider_type_ids: [changes.networkProviderTypeId.currentValue]}, 0, 10000)
        .pipe(finalize(() => this.loading = false))
        .subscribe(({data: networkProviderPlans}) => {
          this.networkProviderPlans = networkProviderPlans;
        });
    }

    if (changes.placeholder) {
      if (this.placeholder === undefined) {
        this.placeholder = this.translateService.instant('Select Network Provider Plan');
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  aceSelectChange = (selected: NetworkProviderPlan | NetworkProviderPlan[]) => {
    this.updateSelectedValue(selected);
    this.onChange(selected);
  };

  aceSelectTouched = () => {
    this.onTouched();
  };

  writeValue(value: any): void {
    this.updateSelectedValue(value);
    this.aceSelect.writeValue(value);
  }

  compareFn(a: NetworkProviderPlan, b: NetworkProviderPlan): boolean {
    return a.network_provider_plan_id === b.network_provider_plan_id;
  }

  groupBy(npp: NetworkProviderPlan): string {
    return npp.network_provider.name;
  }

  searchFn = (term: string, item: NetworkProviderPlan) => {
    term = term.toLocaleLowerCase();
    return (item.name.toLocaleLowerCase().indexOf(term) > - 1) || (this.groupBy(item).toLocaleLowerCase().indexOf(term) > -1);
  };

  private updateSelectedValue(value: NetworkProviderPlan | NetworkProviderPlan[]): void {
    if (value) {
      if (Array.isArray(value)) {
        this.selectedNetworkProviderPlans = value;
      } else {
        this.selectedNetworkProviderPlans = [value];
      }
    } else {
      this.selectedNetworkProviderPlans = <NetworkProviderPlan[]>value;
    }
  }
}
